import {
  Avatar,
  GrammaticalGender,
  SupportedLocale,
  useJoinDemoConferenceMutation,
} from 'generated/graphql';
import { Room, RoomEvent } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';

const LIVEKIT_SERVER_URL = import.meta.env.VITE_LIVEKIT_URL as
  | string
  | undefined;

type UseDemoConferenceOptions = {
  conferenceId: string | undefined;
  onError?: (error: Error) => void;
  onDemoLimitReached?: () => void;
  onDisconnected?: () => void;
};

type JoinDemoConferenceParams = {
  avatar: Avatar;
  allowAgentInterruptions?: boolean;
  locale: SupportedLocale;
  grammaticalGender?: GrammaticalGender;
};

type DemoConferenceState = {
  room: Room | null;
  isJoining: boolean;
  isConnected: boolean;
  error: Error | null;
  remainingDemos: number | null;
  joinDemoConference: (params: JoinDemoConferenceParams) => Promise<void>;
  leaveDemoConference: () => void;
};

export function useDemoConference({
  conferenceId,
  onError,
  onDemoLimitReached,
  onDisconnected,
}: UseDemoConferenceOptions): DemoConferenceState {
  const [room] = useState(
    () =>
      new Room({
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
        adaptiveStream: true,
        dynacast: true,
        audioOutput: {
          deviceId: 'default',
        },
        disconnectOnPageLeave: false,
      })
  );

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isLeavingRef = useRef(false);
  const onDisconnectedRef = useRef(onDisconnected);

  useEffect(() => {
    onDisconnectedRef.current = onDisconnected;
  }, [onDisconnected]);

  // Listen for server-side disconnects (token expiry)
  useEffect(() => {
    const handleDisconnected = () => {
      setIsConnected(false);
      // Only fire onDisconnected if we didn't initiate the disconnect ourselves
      if (!isLeavingRef.current) {
        onDisconnectedRef.current?.();
      }
    };

    room.on(RoomEvent.Disconnected, handleDisconnected);
    return () => {
      room.off(RoomEvent.Disconnected, handleDisconnected);
    };
  }, [room]);

  const [joinDemoConferenceMutation, { loading: isJoining, data: mutationData }] =
    useJoinDemoConferenceMutation({
      onCompleted: async data => {
        try {
          const livekitToken = data.joinDemoConference.token;

          if (!LIVEKIT_SERVER_URL) {
            throw new Error('Missing livekit URL configuration variable!');
          }

          await room.connect(LIVEKIT_SERVER_URL, livekitToken);

          setIsConnected(true);
          setError(null);
        } catch (err) {
          const error =
            err instanceof Error
              ? err
              : new Error('Failed to connect to demo conference');
          setError(error);
          onError?.(error);
        }
      },
      onError: err => {
        if (err.message.includes('Demo limit reached')) {
          onDemoLimitReached?.();
          return;
        }
        const error = new Error(
          `Failed to join demo conference: ${err.message}`
        );
        setError(error);
        onError?.(error);
      },
    });

  const joinDemoConference = useCallback(
    async ({
      avatar,
      allowAgentInterruptions,
      locale,
      grammaticalGender,
    }: JoinDemoConferenceParams) => {
      if (!conferenceId) {
        return;
      }

      if (room.state === 'connected' || room.state === 'connecting') {
        return;
      }

      try {
        setError(null);
        isLeavingRef.current = false;

        const permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        permissionStream.getTracks().forEach(track => track.stop());

        room.localParticipant.setMicrophoneEnabled(true);
        room.localParticipant.setCameraEnabled(true);

        await joinDemoConferenceMutation({
          variables: {
            id: conferenceId,
            input: {
              avatar,
              ...(allowAgentInterruptions !== undefined && {
                allowAgentInterruptions,
              }),
              locale,
              grammaticalGender,
            },
          },
        });
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to get media permissions');
        setError(error);
        onError?.(error);
      }
    },
    [conferenceId, room, joinDemoConferenceMutation, onError]
  );

  const leaveDemoConference = useCallback(() => {
    isLeavingRef.current = true;

    room.localParticipant.trackPublications.forEach(publication => {
      if (publication.track) {
        publication.track.stop();
        if (publication.track.mediaStreamTrack) {
          publication.track.mediaStreamTrack.stop();
        }
      }
    });

    room.disconnect();
    setIsConnected(false);
  }, [room]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      room.localParticipant.trackPublications.forEach(publication => {
        if (publication.track) {
          publication.track.stop();
          if (publication.track.mediaStreamTrack) {
            publication.track.mediaStreamTrack.stop();
          }
        }
      });

      if (room.state !== 'disconnected') {
        room.disconnect();
      }
    };
  }, [room]);

  const remainingDemos = mutationData?.joinDemoConference.remainingDemos ?? null;

  return {
    room,
    isJoining,
    isConnected,
    error,
    remainingDemos,
    joinDemoConference,
    leaveDemoConference,
  };
}
